import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const cleanerIcon = L.divIcon({
  className: "",
  html: `<div style="background:#059669;width:40px;height:40px;border-radius:50%;border:4px solid white;box-shadow:0 4px 14px rgba(5,150,105,0.5);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold">C</div>`,
  iconSize: [40, 40], iconAnchor: [20, 20],
});
const clientIcon = L.divIcon({
  className: "",
  html: `<div style="background:#0f172a;width:36px;height:36px;border-radius:50%;border:4px solid white;box-shadow:0 4px 14px rgba(15,23,42,0.4);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold">H</div>`,
  iconSize: [36, 36], iconAnchor: [18, 18],
});

// Approximate lat/lon per US ZIP (simplified — production would use a geocoder)
const ZIP_TO_COORDS = {
  "10001": [40.7506, -73.9971], "10002": [40.7168, -73.9861], "10003": [40.7317, -73.9895],
  "33101": [25.7743, -80.1937], "94103": [37.7724, -80.4082], "60601": [41.8858, -87.6181],
};

export default function LiveTrackingMap({ postal = "10001", eta = 12 }) {
  const clientPos = ZIP_TO_COORDS[postal] || [40.7506, -73.9971];
  // Start cleaner ~2km away and move progressively closer
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setProgress((p) => (p >= 1 ? 1 : p + 0.03)), 1500);
    return () => clearInterval(t);
  }, []);

  const startPos = useMemo(() => [clientPos[0] + 0.015, clientPos[1] - 0.018], [clientPos]);
  const cleanerPos = useMemo(() => [
    startPos[0] + (clientPos[0] - startPos[0]) * progress,
    startPos[1] + (clientPos[1] - startPos[1]) * progress,
  ], [startPos, clientPos, progress]);

  const remainingEta = Math.max(1, Math.round(eta * (1 - progress)));

  return (
    <div className="relative h-96 rounded-2xl overflow-hidden border border-slate-200" data-testid="live-map">
      <MapContainer center={clientPos} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                   attribution="&copy; OpenStreetMap contributors" />
        <Circle center={clientPos} radius={80} pathOptions={{ color: "#0f172a", fillColor: "#0f172a", fillOpacity: 0.15 }} />
        <Marker position={clientPos} icon={clientIcon}><Popup>Your home</Popup></Marker>
        <Circle center={cleanerPos} radius={40} pathOptions={{ color: "#059669", fillColor: "#059669", fillOpacity: 0.3 }} />
        <Marker position={cleanerPos} icon={cleanerIcon}><Popup>Your cleaner</Popup></Marker>
        <Polyline positions={[cleanerPos, clientPos]} pathOptions={{ color: "#059669", weight: 3, dashArray: "8, 6" }} />
      </MapContainer>
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl px-4 py-3 shadow-lg">
        <div className="text-[10px] font-mono uppercase tracking-widest text-teal-600">Live GPS · ETA</div>
        <div className="font-display font-extrabold text-2xl text-slate-900" data-testid="map-eta">{remainingEta} min</div>
      </div>
    </div>
  );
}
